import { Typography } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { Stack } from '@mui/system'
import { C1Button } from 'c1ui'
import { C1Card } from 'components/core/card/C1Card'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { DisplayAlert, getDisplayAlert } from 'components/pages/explore/inventory/alert-util'
import { InventoryAlertsDrawer } from 'components/pages/explore/inventory/InventoryAlertsDrawer'
import { InventoryAlertStrength } from 'components/pages/explore/inventory/InventoryAlertStrength'
import { useDrawer } from 'hooks/useDrawer'
import { BellRing } from 'lucide-react'
import { AppUserRef } from 'pbts/c1/api/app/v1/app_user'
import {
    InventoryAlertExpandMask,
    SearchInventoryAlertsRequest, SearchInventoryAlertsRequest_Status,
} from 'pbts/c1/api/inventory/v1/inventory_alerts'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { inventoryAlertsSearchApi, InventoryAlertValue } from 'redux_toolkit/apps'

type InventoryAlertsProps = {
    id: string
    appId: string
}

export const InventoryAlerts = ({ id, appId }: InventoryAlertsProps): ReactElement => {
    const intl = useIntl()
    const [showDrawer, closeDrawer] = useDrawer()
    const {
        data,
        isError: isAlertError,
        isLoading: isAlertLoading,
        error: alertFetchError,
    } = inventoryAlertsSearchApi.useSearchInventoryAlerts(SearchInventoryAlertsRequest.create({
        appIds: [appId],
        appUserRefs: [AppUserRef.create({ appId, id })],
        identityAppUserRefs: [AppUserRef.create({ appId, id })],
        status: SearchInventoryAlertsRequest_Status.ACTIVE,
        expandMask: InventoryAlertExpandMask.create({
            paths: ['app_resource', 'app', 'app_resource_type', 'app_user'],
        }),
    }))

    const alerts: readonly InventoryAlertValue[] = data?.[0] || []

    if (isAlertLoading) {
        return <Stack spacing={1} p={2}>
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
        </Stack>
    }

    if (isAlertError) {
        return <APIError
            title={<FormattedMessage id="components_iventory_alerts_fetch_error" defaultMessage="Error loading alerts related to account" />}
            err={alertFetchError}
        />
    }

    const handleLinkClick = (): void => {
        showDrawer(<InventoryAlertsDrawer appId={appId} appUserId={id} closeDrawer={closeDrawer} />)
    }

    const onAlertClick = (alert: DisplayAlert): void => {
        const alertExpanded = alerts.find((a) => a.inventoryAlert?.id === alert?.id)
        if (!alertExpanded) {
            return
        }
        showDrawer(<InventoryAlertsDrawer appId={appId} appUserId={id} expandedAlert={alertExpanded} closeDrawer={closeDrawer} />)
    }

    const alertsToDisplay = alerts.map((alert) => getDisplayAlert(intl, alert)).filter((alert) => alert !== null)

    return <C1Card variant="outlined">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" ml={1} pb={alerts.length > 0 ? 2 : 0}>
            <Stack direction="row" alignItems="center" gap={1}>
                <C1Icon icon={BellRing} sx={{ color: alerts.length > 0 ? 'warning.main' : 'text.disabled' }} />
                {alerts.length > 0 && <Typography variant="h6" fontWeight="bold" component="span">
                    {alerts.length}
                    {' '}
                    <FormattedMessage
                        id="components_iventory_alerts_active_alerts"
                        defaultMessage="active {count, plural, one {alert} other {alerts}}"
                        values={{ count: alerts.length }}
                    />
                </Typography>}
                {alerts.length === 0 && <Typography variant="h6" component="span">
                    <FormattedMessage id="components_iventory_alerts_no_active_alerts" defaultMessage="No active alerts" />
                </Typography>}
            </Stack>
            <C1Button
                variant="text"
                onClick={handleLinkClick}
            >
                <FormattedMessage
                    id="components_inventory_alerts_view_all"
                    defaultMessage="View all alerts"
                />
            </C1Button>
        </Stack>

        {/* Alerts List */}
        <Stack spacing={2}>
            {alertsToDisplay.map((alert) => (alert &&
                <C1Card key={alert.id} variant="outlined" onClick={(): void => onAlertClick(alert)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {alert.title}
                            </Typography>
                            <Typography color="text.secondary" mt={1}>
                                {alert.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" mt={1}>
                                {alert.detectedOn}
                            </Typography>
                        </Stack>
                        <InventoryAlertStrength severity={alert.severity} />
                    </Stack>
                </C1Card>
            ))}
        </Stack>
    </C1Card>
}
